import { Controller } from "@hotwired/stimulus"

// The tournament form's shape depends on the mode it is in.
//
//   grouped FFA          groups all the way through — group size and winner
//                        slots apply, there is no bracket
//   single/double elim   a bracket, optionally opened by a group phase; the
//                        group settings only apply when that is switched on
//
// It also draws what the current settings add up to, so the effect of a group
// size or a threshold is visible before anyone starts the tournament.
export default class extends Controller {
    static targets = ["preview"]
    static values = {
        labels: { type: Object, default: {} },
    }

    // Beyond this the loop is not converging and the settings are wrong.
    static MAX_GROUP_ROUNDS = 6

    connect() {
        this.update()
    }

    update() {
        const mode = this.value("tournament_mode")
        const grouped = mode === "grouped_free_for_all"
        const elimination = mode === "single_elimination" || mode === "double_elimination"
        const phase = elimination && this.checked("group_phase")

        this.toggle("group_phase", elimination)
        this.toggle("group_size", grouped || phase)
        this.toggle("winner_slots", grouped || phase)
        this.toggle("bracket_threshold", phase)

        this.draw({ mode, grouped, elimination, phase })
    }

    // --- the picture ----------------------------------------------------------

    draw({ mode, grouped, elimination, phase }) {
        if (!this.hasPreviewTarget) return

        const teams = this.number("grid_size", 0)
        const size = this.number("group_size", 4)
        const slots = this.number("winner_slots", 2)
        const threshold = this.number("bracket_threshold", 0)

        const stages = []
        let remaining = teams

        if (grouped || phase) {
            // Groups run until few enough teams are left: the final group in a
            // grouped FFA, or the size the bracket takes over at.
            const stop = grouped ? Math.max(size, 2) : Math.max(threshold, 2)

            for (let round = 0; remaining > stop; round++) {
                const groups = Math.ceil(remaining / Math.max(size, 2))
                const advancing = groups * Math.max(slots, 1)

                // Promoting as many teams as went in never ends — the same trap
                // Tournament#group_rounds used to fall into.
                if (advancing >= remaining || round >= this.constructor.MAX_GROUP_ROUNDS) {
                    stages.push({ title: this.label("unbalanced"), note: this.label("unbalanced_hint"), warn: true })
                    remaining = 0
                    break
                }

                stages.push({
                    title: this.label("group_round", { round: round + 1 }),
                    note: this.label("groups_of", { groups, size, advancing }),
                })
                remaining = advancing
            }

            if (grouped && remaining > 0) {
                stages.push({ title: this.label("final_group"), note: this.label("teams", { count: remaining }) })
            }
        }

        if (elimination && remaining > 0) {
            const bracket = phase ? Math.max(threshold, 2) : teams
            const rounds = Math.ceil(Math.log2(Math.max(bracket, 2))) +
                           (mode === "double_elimination" ? 1 : 0)
            stages.push({
                title: this.label(mode === "double_elimination" ? "double_bracket" : "single_bracket"),
                note: this.label("bracket_of", { teams: bracket, rounds }),
            })
        }

        this.previewTarget.innerHTML = ""
        this.previewTarget.hidden = stages.length === 0

        stages.forEach((stage, index) => {
            if (index > 0) this.previewTarget.appendChild(this.arrow())
            this.previewTarget.appendChild(this.stage(stage))
        })
    }

    stage({ title, note, warn }) {
        const box = document.createElement("div")
        box.className = `phase-preview__stage${warn ? " phase-preview__stage--warn" : ""}`
        box.innerHTML = `<strong></strong><span></span>`
        box.querySelector("strong").textContent = title
        box.querySelector("span").textContent = note
        return box
    }

    arrow() {
        const arrow = document.createElement("div")
        arrow.className = "phase-preview__arrow"
        arrow.setAttribute("aria-hidden", "true")
        arrow.textContent = "→"
        return arrow
    }

    // Labels come from the server so the preview speaks the manager's language.
    label(key, replacements = {}) {
        const template = this.labelsValue[key] || key
        return Object.entries(replacements)
                     .reduce((text, [name, value]) => text.replaceAll(`%{${name}}`, value), template)
    }

    // --- fields ---------------------------------------------------------------

    field(name, selector = "") {
        return this.element.querySelector(`[name="tournament[${name}]"]${selector}`)
    }

    value(name) {
        return this.field(name)?.value
    }

    checked(name) {
        return this.field(name, "[type=checkbox]")?.checked ?? false
    }

    number(name, fallback) {
        const parsed = parseInt(this.value(name), 10)
        return Number.isNaN(parsed) ? fallback : parsed
    }

    toggle(name, visible) {
        const row = this.field(name)?.closest(".form-group")
        if (row) row.hidden = !visible
    }
}
